import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import clsx from 'clsx';
import React from 'react';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {emitEventLog} from '@di-shared/features/analytics';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';

export enum BatteryStatusType {
  NORMAL,
  DISCHARGE_HAZARD,
  DETERIORATION,
  NONE,
}

function BatteryStatusCheck(dischargeRisk: boolean, aged: boolean, statusDetected: boolean) {
  if (aged) {
    return BatteryStatusType.DETERIORATION;
  }
  if (!statusDetected) {
    return BatteryStatusType.NONE;
  }
  if (dischargeRisk) {
    return BatteryStatusType.DISCHARGE_HAZARD;
  }
  return BatteryStatusType.NORMAL;
}

export interface VehicleBatteryKrProps {
  vin: string;
}

function VehicleBatteryKr({vin}: VehicleBatteryKrProps) {
  const {t} = useInferableTranslation();

  const {data: batteryData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.battery.getBatteryStatus3), vin],
    queryFn: () => diApi.battery.getBatteryStatus3({vin}),
    select: ({data}) => data.batteryStatus,
  });

  const batteryStatusCheck = BatteryStatusCheck(batteryData.dischargeRisk, batteryData.aged, batteryData.statusDetected);

  return (
    <li className="car_diagnosis_item">
      <LinkTo
        className="car_diagnosis_link"
        to={ROUTE_PATHS.DIAG_BATTERY.getFullPath(vin)}
        onClickBypass={() =>
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'view 12v battery',
            click_text: t('T30077'),
            click_location: 'body',
          })
        }
      >
        <div className="section_container">
          <strong className="car_diagnosis_title">{t('T30077')}</strong>

          {batteryData?.statusDetected && (
            <div className="badge_box">
              <span
                className={clsx('badge badge_20px', {
                  badge_basic_container: batteryStatusCheck === BatteryStatusType.NORMAL,
                  badge_container_red:
                    batteryStatusCheck === BatteryStatusType.DISCHARGE_HAZARD || batteryStatusCheck === BatteryStatusType.DETERIORATION,
                })}
              >
                {batteryStatusCheck === BatteryStatusType.NORMAL && t('T20084')}
                {batteryStatusCheck === BatteryStatusType.DISCHARGE_HAZARD && t('T20085')}
                {batteryStatusCheck === BatteryStatusType.DETERIORATION && t('T20081')}
              </span>
            </div>
          )}
          <span
            className={clsx('ic', {
              ic_battery_good: batteryStatusCheck === BatteryStatusType.NORMAL,
              ic_battery_warning: batteryStatusCheck !== BatteryStatusType.NORMAL,
            })}
          />
        </div>
      </LinkTo>
    </li>
  );
}

VehicleBatteryKr.Error = VehicleBatteryError;
export default VehicleBatteryKr;

function VehicleBatteryError() {
  const {t} = useInferableTranslation();
  const {openAlert} = useCcWebviewUtilsHelper();
  return (
    <li className="car_diagnosis_item">
      <div className="car_diagnosis_link">
        <div
          className="section_container"
          role="button"
          onClick={() =>
            openAlert({
              title: t('T30077'),
              message: t('T30358') + '\n' + t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
            })
          }
        >
          <strong className="car_diagnosis_title">{t('T30077')}</strong>
          <span className="ic ic_battery_warning" />
        </div>
      </div>
    </li>
  );
}
